<template>
<div id="songs">
  <v-container>
    <v-row justify="center">
      <v-col md="8">
        <h1>Songs</h1>
        <v-data-table
          v-model="selectedSongs"
          :headers="headers"
          :items="songs"
          :page.sync="page"
          :options.sync="options"
          :server-items-length="totalCount"
          :loading="loading"
          :items-per-page="itemsPerPage"
          item-key="id"
          class="elevation-1"
          hide-default-footer
          :show-select="isAuthenticated"
          @page-count="pageCount = $event"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-text-field
                v-model="songsSearch"
                clearable
                append-icon="mdi-magnify"
                label="Search"
                @click:append="loadData"
                @keydown.enter="loadData"
                class="mr-4 mt-7"
                rounded
                outlined
                dense
              ></v-text-field>
      
              <v-dialog
                v-model="isEditDialogVisible"
                max-width="300px"
                v-if="isAuthenticated && selectedSongs.length === 0"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                  >
                    New Song
                  </v-btn>
                </template>
                <SongEditCard
                  :save="save"
                  :close="close"
                  :performers="performers"
                  :categories="categories"
                  :edited-item="editedItem"
                  :default-item="defaultItem"
                ></SongEditCard>
              </v-dialog>
              
              <SongAddToCategoriesDialog
                :categories.sync="categories"
                :selected-songs="selectedSongsIDs"
                @done="selectedSongs = []"
                v-else-if="isAuthenticated"
              ></SongAddToCategoriesDialog>
              
              <v-dialog v-model="dialogDelete" max-width="500px">
                <SongDeleteCard
                  :title="editedItem.title"
                  :close="closeDelete"
                  :confirm="deleteItemConfirm"
                ></SongDeleteCard>
              </v-dialog>
            </v-toolbar>
          </template>
          
          <template v-slot:[`item.actions`]="{ item }" v-if="isAuthenticated">
            <v-icon class="mr-2" @click="editItem(item)">
              mdi-pencil
            </v-icon>
            <v-icon color="red darken-4" @click="deleteItem(item)"> mdi-delete </v-icon>
          </template>
        </v-data-table>
        <div class="text-center pt-2">
          <v-pagination v-model="page" :length="pageCount"></v-pagination>
        </div>
      </v-col>
    </v-row>
  </v-container>
</div>
</template>

<script>
import { mapGetters } from "vuex";
import apiClient from "@/utils/apiClient";
import SongEditCard from "@/views/Songs/SongEditCard";
import SongDeleteCard from "@/views/Songs/SongDeleteCard";
import SongAddToCategoriesDialog from "@/views/Songs/SongAddToCategoriesDialog";

export default {
  name: "Songs",
  components: {SongAddToCategoriesDialog, SongDeleteCard, SongEditCard },
  data: () => ({
    totalCount: 0,
    loading: true,
    options: {},
    page: 1,
    pageCount: 0,
    itemsPerPage: 30,
    headers: [
      {
        text: "Title",
        value: "title",
      },
      {
        text: "Performer",
        value: "performerName",
      },
    ],
    songs: [],
    selectedSongs: [],
    songsSearch: "",
    
    isEditDialogVisible: false,
    dialogDelete: false,
    categories: [],
    performers: [],
    
    editedIndex: -1,
    editedItem: {
      id: null,
      title: "",
      performerID: "",
      categories: [],
    },
    defaultItem: {
      id: null,
      title: "",
      performerID: "",
      categories: [],
    },
    defaultActionsHeader: {
      text: "Actions",
      value: "actions",
      sortable: false,
    },
  }),
  
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Song" : "Edit Song";
    },
    selectedSongsIDs() {
      return this.selectedSongs.map(x => x.id)
    },
    ...mapGetters("auth", ["isAuthenticated"]),
  },
  
  mounted() {
    if (this.isAuthenticated) {
      this.headers.push(this.defaultActionsHeader);
    }
  },
  
  watch: {
    options: {
      handler() {
        this.loadData();
      },
      deep: true,
    },
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    isAuthenticated() {
      this.headers.push(this.defaultActionsHeader);
    },
  },
  
  methods: {
    loadData() {
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const options = {
        page,
        itemsPerPage,
        sortBy: sortBy[0] || null,
        sortDesc: sortDesc[0] || null,
        search: this.songsSearch,
      };
      apiClient
        .getSongs(options)
        .then((response) => {
          this.loading = false;
          this.totalCount = response.data.totalCount;
          this.songs = response.data.items;
          this.generatePerformersAndCategoriesFromSongs(this.songs);
        })
        .catch((error) => {
          this.loading = false;
          console.log(error);
        });
    },
    
    generatePerformersAndCategoriesFromSongs(songs) {
      let performersIDs = this.performers.map((x) => x.id);
      let categoriesIDs = this.categories.map((x) => x.id);
      songs.forEach((song) => {
        if (performersIDs.indexOf(song.performerID) < 0) {
          this.performers.push({
            id: song.performerID,
            name: song.performerName,
          });
          performersIDs.push(song.performerID);
        }
        song.categories.forEach((category) => {
          if (categoriesIDs.indexOf(category.id) < 0) {
            this.categories.push({ id: category.id, name: category.name });
            categoriesIDs.push(category.id);
          }
        });
      });
    },
    
    editItem(item) {
      this.editedIndex = this.songs.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.editedItem.categories = this.editedItem.categories.map((v) => v.id);
      this.isEditDialogVisible = true;
    },
    
    deleteItem(item) {
      this.editedIndex = this.songs.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.editedItem.categories = this.editedItem.categories.map((v) => v.id);
      this.dialogDelete = true;
    },
    
    deleteItemConfirm() {
      const { id } = this.editedItem;
      const song = { id };
      apiClient
        .deleteSong(song)
        .then(() => {
          this.loadData();
          this.closeDelete();
        })
        .catch((error) => {
          console.log(error);
        });
    },
    
    close() {
      this.isEditDialogVisible = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    
    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    
    save() {
      const { id, title, performerID: performer, categories } = this.editedItem;
      const song = { id, title, performer, categories };
      if (this.editedIndex > -1) {
        // Edit
        apiClient
          .updateSong(song)
          .then(() => {
            this.loadData();
            this.close();
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        // Create
        apiClient
          .createSong(song)
          .then(() => {
            this.loadData();
            this.close();
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
  },
};
</script>
